// src/Views/Referral/Referral.js
// 🔥 Refer & Earn Page - Auto-generates referral code for existing users

import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import { 
    doc, 
    onSnapshot, 
    collection, 
    query, 
    where, 
    orderBy, 
    limit, 
    getDocs,
    updateDoc 
} from 'firebase/firestore';
import './Referral.css';

const Referral = () => {
    const { user } = useAuth();
    const [userData, setUserData] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(true);

    // Base URL for referral links
    const BASE_URL = "https://aipredict.app";

    // 🔥 Generate unique referral code
    const generateReferralCode = () => {
        const prefix = (user?.email?.split('@')[0] || 'USR').substring(0, 3).toUpperCase();
        const random = Math.random().toString(36).substring(2, 7).toUpperCase();
        return `${prefix}${random}`;
    };

    // Listen to user data
    useEffect(() => {
        if (!user?.uid) return;

        const unsub = onSnapshot(doc(db, 'users', user.uid), async (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                
                // 🔥 AUTO-GENERATE: If user doesn't have referral fields, create them
                if (!data.referralCode) {
                    const newCode = generateReferralCode();
                    try {
                        await updateDoc(doc(db, 'users', user.uid), {
                            referralCode: newCode,
                            referralPoints: data.referralPoints || 0,
                            referralCount: data.referralCount || 0,
                            referralSubscribers: data.referralSubscribers || 0,
                            referralRewardsEarned: data.referralRewardsEarned || 0,
                            milestonesAwarded: data.milestonesAwarded || [],
                        });
                        console.log('✅ Generated referral code:', newCode);
                        // The onSnapshot will trigger again with updated data
                    } catch (error) {
                        console.error('Error generating referral code:', error);
                    }
                } else {
                    setUserData(data);
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        });

        return () => unsub();
    }, [user?.uid]);

    // Fetch leaderboard directly from Firestore
    useEffect(() => {
        const fetchLeaderboard = async () => {
            try {
                const q = query(
                    collection(db, 'users'),
                    where('referralCount', '>', 0),
                    orderBy('referralCount', 'desc'),
                    limit(10)
                );
                const snapshot = await getDocs(q);
                const leaders = [];
                let rank = 1;
                snapshot.forEach((doc) => {
                    const data = doc.data();
                    leaders.push({
                        rank: rank++,
                        displayName: data.displayName || data.email?.split('@')[0] || 'Anonymous',
                        referralCount: data.referralCount || 0,
                        referralSubscribers: data.referralSubscribers || 0,
                    });
                });
                setLeaderboard(leaders);
            } catch (error) {
                console.error("Error fetching leaderboard:", error);
            }
        };

        fetchLeaderboard();
    }, []);

    // Copy referral link
    const copyLink = () => {
        const link = `${BASE_URL}/join/${userData?.referralCode || ''}`;
        navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Copy referral code
    const copyCode = () => {
        navigator.clipboard.writeText(userData?.referralCode || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Share functions
    const shareWhatsApp = () => {
        const link = `${BASE_URL}/join/${userData?.referralCode || ''}`;
        const text = `🚀 Join me on AiPredict - AI-powered football & crypto predictions! Use my referral link and get bonus trial days: ${link}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    };

    const shareTwitter = () => {
        const link = `${BASE_URL}/join/${userData?.referralCode || ''}`;
        const text = `🚀 I'm using AiPredict for AI-powered predictions! Join with my link and get bonus days:`;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`, '_blank');
    };

    const shareTelegram = () => {
        const link = `${BASE_URL}/join/${userData?.referralCode || ''}`;
        const text = `🚀 Join me on AiPredict - AI-powered football & crypto predictions! Use my referral link:`;
        window.open(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`, '_blank');
    };

    // Calculate progress to next milestone
    const getNextMilestone = () => {
        const points = userData?.referralPoints || 0;
        const milestones = [
            { points: 50, reward: 7 },
            { points: 100, reward: 14 },
            { points: 200, reward: 30 },
        ];

        for (const m of milestones) {
            if (points < m.points) {
                return { target: m.points, reward: m.reward, current: points };
            }
        }
        return { target: 200, reward: 30, current: points, completed: true };
    };

    const milestone = getNextMilestone();
    const progressPercent = Math.min((milestone.current / milestone.target) * 100, 100);

    if (loading) {
        return (
            <div className="referral">
                <div className="referral__loading">
                    <Icon icon="solar:refresh-circle-linear" className="referral__loading-icon" />
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="referral">
            {/* Header */}
            <div className="referral__header">
                <div className="referral__header-content">
                    <h1 className="referral__title">
                        <Icon icon="solar:gift-bold" className="referral__title-icon" />
                        Refer & Earn
                    </h1>
                    <p className="referral__subtitle">
                        Invite friends and earn free premium days!
                    </p>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="referral__stats">
                <div className="referral__stat-card">
                    <Icon icon="solar:users-group-rounded-bold" className="referral__stat-icon referral__stat-icon--blue" />
                    <div className="referral__stat-content">
                        <span className="referral__stat-value">{userData?.referralCount || 0}</span>
                        <span className="referral__stat-label">Total Referrals</span>
                    </div>
                </div>

                <div className="referral__stat-card">
                    <Icon icon="solar:medal-star-bold" className="referral__stat-icon referral__stat-icon--yellow" />
                    <div className="referral__stat-content">
                        <span className="referral__stat-value">{userData?.referralPoints || 0}</span>
                        <span className="referral__stat-label">Points Earned</span>
                    </div>
                </div>

                <div className="referral__stat-card">
                    <Icon icon="solar:crown-bold" className="referral__stat-icon referral__stat-icon--purple" />
                    <div className="referral__stat-content">
                        <span className="referral__stat-value">{userData?.referralSubscribers || 0}</span>
                        <span className="referral__stat-label">Subscribers</span>
                    </div>
                </div>

                <div className="referral__stat-card">
                    <Icon icon="solar:calendar-bold" className="referral__stat-icon referral__stat-icon--green" />
                    <div className="referral__stat-content">
                        <span className="referral__stat-value">{userData?.referralRewardsEarned || 0}</span>
                        <span className="referral__stat-label">Days Earned</span>
                    </div>
                </div>
            </div>

            {/* Referral Code Section */}
            <div className="referral__code-section">
                <h2 className="referral__section-title">Your Referral Code</h2>
                
                <div className="referral__code-box">
                    <span className="referral__code">{userData?.referralCode || 'Generating...'}</span>
                    <button className="referral__copy-btn" onClick={copyCode} disabled={!userData?.referralCode}>
                        <Icon icon={copied ? "solar:check-circle-bold" : "solar:copy-bold"} />
                        {copied ? 'Copied!' : 'Copy'}
                    </button>
                </div>

                <div className="referral__link-box">
                    <span className="referral__link">{BASE_URL}/join/{userData?.referralCode || '...'}</span>
                    <button className="referral__copy-btn" onClick={copyLink} disabled={!userData?.referralCode}>
                        <Icon icon={copied ? "solar:check-circle-bold" : "solar:link-bold"} />
                        {copied ? 'Copied!' : 'Copy Link'}
                    </button>
                </div>

                {/* Share Buttons */}
                <div className="referral__share-buttons">
                    <button className="referral__share-btn referral__share-btn--whatsapp" onClick={shareWhatsApp}>
                        <Icon icon="simple-icons:whatsapp" />
                        WhatsApp
                    </button>
                    <button className="referral__share-btn referral__share-btn--twitter" onClick={shareTwitter}>
                        <Icon icon="simple-icons:x" />
                        Twitter
                    </button>
                    <button className="referral__share-btn referral__share-btn--telegram" onClick={shareTelegram}>
                        <Icon icon="simple-icons:telegram" />
                        Telegram
                    </button>
                </div>
            </div>

            {/* Progress to Next Milestone */}
            <div className="referral__progress-section">
                <h2 className="referral__section-title">Progress to Next Reward</h2>
                
                <div className="referral__progress-card">
                    {milestone.completed ? (
                        <div className="referral__progress-complete">
                            <Icon icon="solar:crown-bold" className="referral__crown-icon" />
                            <p>You've reached all milestones! Keep referring to help friends discover AiPredict.</p>
                        </div>
                    ) : (
                        <>
                            <div className="referral__progress-header">
                                <span>{milestone.current} / {milestone.target} referrals</span>
                                <span className="referral__progress-reward">+{milestone.reward} days premium</span>
                            </div>
                            <div className="referral__progress-bar">
                                <div 
                                    className="referral__progress-fill" 
                                    style={{ width: `${progressPercent}%` }}
                                />
                            </div>
                            <p className="referral__progress-hint">
                                {milestone.target - milestone.current} more referrals to unlock +{milestone.reward} premium days!
                            </p>
                        </>
                    )}
                </div>
            </div>

            {/* How It Works */}
            <div className="referral__how-it-works">
                <h2 className="referral__section-title">How It Works</h2>
                
                <div className="referral__steps">
                    <div className="referral__step">
                        <div className="referral__step-number">1</div>
                        <div className="referral__step-content">
                            <h3>Share Your Link</h3>
                            <p>Send your unique referral link to friends</p>
                        </div>
                    </div>

                    <div className="referral__step">
                        <div className="referral__step-number">2</div>
                        <div className="referral__step-content">
                            <h3>Friends Sign Up</h3>
                            <p>They get +1 bonus trial day, you get +1 point</p>
                        </div>
                    </div>

                    <div className="referral__step">
                        <div className="referral__step-number">3</div>
                        <div className="referral__step-content">
                            <h3>They Subscribe</h3>
                            <p>You get +3 days premium (or +7 for yearly!)</p>
                        </div>
                    </div>

                    <div className="referral__step">
                        <div className="referral__step-number">4</div>
                        <div className="referral__step-content">
                            <h3>Hit Milestones</h3>
                            <p>50 refs = +7 days, 100 = +14, 200 = +30 days!</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Rewards Table */}
            <div className="referral__rewards">
                <h2 className="referral__section-title">Rewards</h2>
                
                <div className="referral__rewards-table">
                    <div className="referral__reward-row referral__reward-row--header">
                        <span>Action</span>
                        <span>Reward</span>
                    </div>
                    <div className="referral__reward-row">
                        <span>Friend signs up</span>
                        <span className="referral__reward-value">+1 point</span>
                    </div>
                    <div className="referral__reward-row">
                        <span>Friend subscribes (monthly/weekly)</span>
                        <span className="referral__reward-value">+3 days premium</span>
                    </div>
                    <div className="referral__reward-row">
                        <span>Friend subscribes (yearly)</span>
                        <span className="referral__reward-value">+7 days premium</span>
                    </div>
                    <div className="referral__reward-row referral__reward-row--milestone">
                        <span>🏆 50 referral points</span>
                        <span className="referral__reward-value">+7 days premium</span>
                    </div>
                    <div className="referral__reward-row referral__reward-row--milestone">
                        <span>🏆 100 referral points</span>
                        <span className="referral__reward-value">+14 days premium</span>
                    </div>
                    <div className="referral__reward-row referral__reward-row--milestone">
                        <span>🏆 200 referral points</span>
                        <span className="referral__reward-value">+30 days premium</span>
                    </div>
                </div>
            </div>

            {/* Leaderboard */}
            {leaderboard.length > 0 && (
                <div className="referral__leaderboard">
                    <h2 className="referral__section-title">
                        <Icon icon="solar:cup-star-bold" className="referral__section-icon" />
                        Top Referrers
                    </h2>
                    
                    <div className="referral__leaderboard-list">
                        {leaderboard.map((entry, index) => (
                            <div key={index} className={`referral__leaderboard-item ${index < 3 ? 'referral__leaderboard-item--top' : ''}`}>
                                <span className={`referral__leaderboard-rank referral__leaderboard-rank--${index + 1}`}>
                                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                                </span>
                                <span className="referral__leaderboard-name">{entry.displayName}</span>
                                <span className="referral__leaderboard-count">{entry.referralCount} referrals</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Referral;